/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: We need a way to cancel a request (throw away its response) so we
// can listen on the store for "clear" event and reset all our state, cancelling
// pending requests.
import { showError } from '../components/App/AppAlert';
import DataStore from './DataStore';
import { fetchJSON, postJSON } from './Fetch';
import { resolveKey } from './helpers/resolveKey';
import { getResource, ResourceKey } from './constants/resources';

const dataStore = DataStore.getInstance('app');
const currentlyFetching: Map<string, Promise<ObjectOf<any>>> = new Map();
const currentlySaving: Map<string, number> = new Map();
const fetchedData = new Map<string, unknown>();
const dirtyFields = new Set();
const subscriptions = new Set<() => void>();

dataStore.on('change', (key) => {
  // If we are tracking this field (meaning we have ever fetched it) then we
  // should mark it as dirty on change.
  if (fetchedData.has(key as string)) {
    dirtyFields.add(key);
  }
});

// TODO: key should be ResourceKey
export function hasFetchEndpoint(key: string) {
  let resource = getResource(key as ResourceKey);
  return resource != null && resource.fetch != null;
}

// TODO: key should be ResourceKey
export function hasSaveEndpoint(key: string) {
  let resource = getResource(key as ResourceKey);
  return resource != null && resource.save != null;
}

export function isFetching(key: string, params: Array<string>) {
  let resolvedKey = resolveKey(key, params);
  return currentlyFetching.has(resolvedKey);
}

export function isSaving(key: string, params: Array<string>) {
  let resolvedKey = resolveKey(key, params);
  let count = currentlySaving.get(resolvedKey) || 0;
  return count !== 0;
}

export function hasDirty() {
  return dirtyFields.size !== 0;
}

export function isDirty(key: string, params: Array<string>) {
  let resolvedKey = resolveKey(key, params);
  return dirtyFields.has(resolvedKey);
}

export function incrementIsSaving(resolvedKey: string, increment: number) {
  let count = currentlySaving.get(resolvedKey) || 0;
  count += increment;
  if (count === 0) {
    currentlySaving.delete(resolvedKey);
  } else {
    currentlySaving.set(resolvedKey, count + increment);
  }
}

export function subscribe(listener: () => void) {
  subscriptions.add(listener);
  return {
    unsubscribe: () => {
      subscriptions.delete(listener);
    },
  };
}

function notifyAll() {
  for (let listener of subscriptions) {
    listener();
  }
}

export function fetchData(
  // TODO: key should be ResourceKey
  key: string,
  params: Array<string>,
): Promise<{
  [key: string]: any;
}> {
  let resource = getResource(key as ResourceKey);
  if (!resource || !resource.fetch) {
    return Promise.resolve({});
  }
  let { endpoint, responseTransform } = resource.fetch;
  let resolvedKey = resolveKey(key, params);
  let existing = currentlyFetching.get(resolvedKey);
  if (existing != null) {
    return existing;
  }
  let resolvedEndpoint = resolveKey(endpoint, params);
  let promise = fetchJSON(
    resolvedEndpoint,
    (
      error: Error | null | undefined,
      data: {
        [key: string]: any;
      },
    ) => {
      currentlyFetching.delete(resolvedKey);
      // Update the dataStore before we notifyAll()
      if (!error) {
        if (responseTransform) {
          data = responseTransform(data) as any;
        }
        // TODO: Fix this
        dataStore.set(resolvedKey as any, data);
        fetchedData.set(resolvedKey, data);
        dirtyFields.delete(resolvedKey);
      }
      notifyAll();
      if (error) {
        showError(error.message);
      }
    },
  );
  currentlyFetching.set(resolvedKey, promise);
  notifyAll();
  return promise;
}

export function saveData(
  key: string,
  params: Array<string>,
): Promise<{
  [key: string]: any;
}> {
  let resource = getResource(key as ResourceKey);
  if (!resource || !resource.save) {
    return Promise.resolve({});
  }
  let {
    endpoint,
    requestTransform,
    responseTransform,
    afterSave,
  } = resource.save;
  let resolvedKey = resolveKey(key, params);
  // TODO: Fix this
  let data: unknown = dataStore.get(resolvedKey as any);
  incrementIsSaving(resolvedKey, 1);
  notifyAll();
  let resolvedEndpoint = resolveKey(endpoint, params);
  let requestData = requestTransform
    ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      requestTransform(data, fetchedData.get(resolvedKey))
    : data;
  return postJSON(resolvedEndpoint, requestData, (error, responseData) => {
    incrementIsSaving(resolvedKey, -1);
    // Give afterSave() a chance to do it's thing before we notifyAll()
    if (!error) {
      if (responseTransform) {
        responseData = responseTransform(responseData);
      }
      if (afterSave) {
        afterSave(resolvedKey, responseData);
      }
      dirtyFields.delete(resolvedKey);
    }
    notifyAll();
    if (error) {
      showError(responseData?.error ? responseData.error : error.message);
    }
  });
}

export function clearDirty(key: string, params: Array<string>) {
  let resolvedKey = resolveKey(key, params);
  dirtyFields.delete(resolvedKey);
}
